/**
 * Loaders + types for the per-concelho SEO pages. The three artifacts are baked
 * by scripts/seo/build_concelhos.py and shipped in public/data/ (self-contained,
 * same-origin, no API/R2 dependency). See SEO_CONTENT_PLAN.md Phase 0.
 */
import type { RiskLevel } from '../types';

export interface FireHistory {
  n_fires: number;
  area_ha: number;
  worst_year: number;
  peak_month: number;
  since_year: number;
}

export interface Concelho {
  slug: string;
  dico: string;
  nome: string;
  distrito: string;
  distrito_slug: string;
  nuts3: string;
  area_km2: number;
  n_cells: number;
  centroid: [number, number]; // [lat, lon]
  bbox: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
  fire_history: FireHistory | null;
  neighbours: string[];
}

export interface Distrito {
  slug: string;
  nome: string;
  area_km2: number;
  n_concelhos: number;
  concelhos: string[];
}

export interface ConcelhoLive {
  slug: string;
  n_cells: number;
  risk: Record<RiskLevel, number>;
  mod_plus: number;
  mod_plus_pct: number;
  mean_prob: number;
  max_prob: number;
  dominant: RiskLevel;
  // Briefing enrichment (present when the doc comes from the 3h R2 bake,
  // scripts/bake_concelhos_live.py; absent in the build-time snapshot).
  anomaly_mean?: number | null;
  anomaly_max?: number | null;
  forecast24h?: {
    for_date: string;
    risk: Record<RiskLevel, number>;
    mod_plus_pct: number;
    dominant: RiskLevel;
    trend: 'sobe' | 'desce' | 'estável';
  };
  delta?: { vs_date: string; mod_plus_pct: number; max_prob: number };
  rcm?: { d0: number | null; d1: number | null };
  fires_7d?: {
    count: number;
    items: { start_datetime: string; burned_area_ha: number | null; source: string }[];
  };
  /**
   * Per-freguesia breakdown, hottest first (mean_prob desc). Present from the
   * 2026-07-15 bake onward; absent if the assignment parquet predates the freguesia
   * PIP, so treat it as optional rather than assuming it.
   */
  freguesias?: {
    nome: string;
    dtmnfr: string | null;
    n_cells: number;
    risk: Record<RiskLevel, number>;
    mod_plus: number;
    mod_plus_pct: number;
    mean_prob: number;
    max_prob: number;
    dominant: RiskLevel;
    anomaly_mean: number | null;
  }[];
}

export interface StaticMeta {
  generated_at: string;
  caop_edition: string;
  caop_licence: string;
  attribution: string;
  n_concelhos: number;
  n_distritos: number;
  fires_source: string;
}

export interface StaticDoc {
  meta: StaticMeta;
  concelhos: Concelho[];
}

export interface DistritosDoc {
  meta: StaticMeta;
  distritos: Distrito[];
}

export interface LiveDoc {
  for_date: string;
  prediction_time: string;
  model_version: string;
  generated_at: string;
  degraded?: boolean; // serving weather in seasonal fallback (R2 bake only)
  concelhos: ConcelhoLive[];
}

const base = import.meta.env.BASE_URL || '/';

async function loadJson<T>(name: string): Promise<T> {
  const res = await fetch(`${base}data/${name}`);
  if (!res.ok) throw new Error(`Failed to load ${name}: ${res.status}`);
  return res.json() as Promise<T>;
}

export const loadConcelhos = () => loadJson<StaticDoc>('concelhos_static.json');
export const loadDistritos = () => loadJson<DistritosDoc>('distritos_static.json');
export const loadLive = () => loadJson<LiveDoc>('concelhos_live.json');

/**
 * Freshest live doc: the 3h R2 bake when available (carries the briefing
 * enrichment: anomaly, forecast trend, deltas, official RCM, fires), falling
 * back to the bundled build-time snapshot.
 */
export async function loadLiveFresh(): Promise<LiveDoc> {
  const cdn = import.meta.env.VITE_DATA_URL;
  if (cdn) {
    try {
      const res = await fetch(`${cdn}/concelhos_live.json`);
      if (res.ok) return (await res.json()) as LiveDoc;
    } catch {
      // fall through to the bundled snapshot
    }
  }
  return loadLive();
}

// --- helpers --------------------------------------------------------------

export const RISK_PT: Record<RiskLevel, string> = {
  Low: 'Baixo',
  Moderate: 'Moderado',
  High: 'Alto',
  Extreme: 'Extremo',
};

const MONTHS_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

export const monthName = (m: number): string => MONTHS_PT[m - 1] ?? '';

/** Portuguese thousands formatting (1619 -> "1 619"). */
export const fmtInt = (n: number): string =>
  Math.round(n).toLocaleString('pt-PT').replace(/,/g, ' ');

/** Compact area: 13131.6 ha -> "13 132 ha" (or "km²" above 100k ha). */
export function fmtArea(ha: number): string {
  if (ha >= 100_000) return `${fmtInt(ha / 100)} km²`;
  return `${fmtInt(ha)} ha`;
}

/** Format an ISO date/time as "11 jul 2026, 18:30". */
export function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
